import type { FormEvent, MouseEvent } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { useAutoLinkContext, useAutoNavigate } from '@betterment/js-runtime';

import { isEchoForm } from '../dom/form';

import type { EchoSubmitFn } from '../types';

type Props = {
  className?: string;
  slot: DocumentFragment;
  onSubmit: EchoSubmitFn;
};

function isModifiedClick(event: MouseEvent): boolean {
  return event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

function findLink(event: MouseEvent): HTMLAnchorElement | null {
  const target = event.target as HTMLElement | null;
  return (
    target &&
    target.closest(
      'a[href]:not([target^=_]):not([download]):not([data-echo="false"]):not([data-remote="true"]):not([data-method])'
    )
  );
}

export function EchoSlot({ className, slot, onSubmit }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useAutoNavigate();
  const { matchReactRoute } = useAutoLinkContext();

  useLayoutEffect(() => {
    ref.current?.replaceChildren(slot.cloneNode(true));
  }, [slot]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      if (isModifiedClick(event)) return;

      const link = findLink(event);
      if (!link) return;

      const match = matchReactRoute(link.href);
      if (!match) return;

      event.preventDefault();
      void navigate(link.href);
    },
    [matchReactRoute, navigate]
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLDivElement>) => {
      const form = event.target as HTMLFormElement;
      const { submitter } = event.nativeEvent as SubmitEvent;

      if (event.defaultPrevented) return;
      if (!isEchoForm(form)) return;

      event.preventDefault();
      event.stopPropagation();

      void onSubmit(form, submitter);
    },
    [onSubmit]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={className} ref={ref} onClick={handleClick} onSubmit={handleSubmit} />
  );
}
